import { Videocam as VideocamIcon } from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import VideoCall from './VideoCall';

interface IncomingCallNotificationProps {
  currentUserId: string;
  isDietitian: boolean;
}

interface IncomingCall {
  id: string;
  from: string;
  fromName: string;
  conversationId: string;
}

const IncomingCallNotification: React.FC<IncomingCallNotificationProps> = ({
  currentUserId,
  isDietitian,
}) => {
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [isVideoCallOpen, setIsVideoCallOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Gelen aramaları dinle
    const callsQuery = query(
      collection(db, 'videoCalls'),
      where('to', '==', currentUserId),
      where('status', '==', 'calling')
    );

    const unsubscribe = onSnapshot(callsQuery, async (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const callData = change.doc.data();

          // Arayan kişinin adını al (zaten callData'da mevcut)
          const fromName =
            callData.fromName || (isDietitian ? 'Danışan' : 'Diyetisyen');

          setIncomingCall({
            id: change.doc.id,
            from: callData.from,
            fromName: fromName,
            conversationId: callData.conversationId,
          });

          // Ses çal (isteğe bağlı)
          playRingtone();
        }
      });
    });

    return () => unsubscribe();
  }, [currentUserId, isDietitian]);

  const playRingtone = () => {
    // Basit bir bildirim sesi
    try {
      const audio = new Audio(
        'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSA0PVqzn77BdGAg+ltryxnMpBSl+zPLaizsIGGS57OihUBELTKXh8bllHAU2jdXzzn0vBSCAz/PajDkIGGS67OihUBELTKXh8bllHAU2jdXzzn0vBSCAz/PajDkIGGS67OihUBELTKXh8bllHAU2jdXy'
      );
      audio.play().catch((error) => {
        console.log('Ses çalınamadı:', error);
      });
    } catch (error) {
      console.log('Ses çalınamadı:', error);
    }
  };

  const acceptCall = async () => {
    if (incomingCall) {
      try {
        // Arama durumunu 'accepted' olarak güncelle
        await updateDoc(doc(db, 'videoCalls', incomingCall.id), {
          status: 'accepted',
        });
      } catch (error) {
        console.error('Arama kabul etme hatası:', error);
      }
    }
    setIsVideoCallOpen(true);
    stopRingtone();
  };

  const rejectCall = async () => {
    if (incomingCall) {
      try {
        await updateDoc(doc(db, 'videoCalls', incomingCall.id), {
          status: 'rejected',
        });
      } catch (error) {
        console.error('Arama reddetme hatası:', error);
      }
      setIncomingCall(null);
      stopRingtone();
    }
  };

  const stopRingtone = () => {
    // Ses durdurma mantığı
  };

  const handleVideoCallClose = () => {
    setIsVideoCallOpen(false);
    setIncomingCall(null);
  };

  if (!incomingCall && !isVideoCallOpen) {
    return null;
  }

  return (
    <>
      {/* Gelen Arama Bildirimi */}
      {incomingCall && !isVideoCallOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            p: 3,
            zIndex: 9999,
            minWidth: 320,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #43A047 0%, #66BB6A 100%)',
            color: 'white',
            animation: 'slideInBounce 0.5s ease-out',
            '@keyframes slideInBounce': {
              '0%': {
                transform: 'translateX(400px)',
                opacity: 0,
              },
              '60%': {
                transform: 'translateX(-20px)',
                opacity: 1,
              },
              '80%': {
                transform: 'translateX(10px)',
              },
              '100%': {
                transform: 'translateX(0)',
              },
            },
            boxShadow: '0 8px 32px rgba(67, 160, 71, 0.4)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.8,
                  },
                },
              }}
            >
              <VideocamIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                📞 Gelen Arama
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {incomingCall.fromName}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={acceptCall}
              sx={{
                bgcolor: 'white',
                color: '#43A047',
                fontWeight: 700,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              ✓ Kabul Et
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={rejectCall}
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 700,
                py: 1.5,
                fontSize: '1rem',
                borderWidth: 2,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  borderWidth: 2,
                },
                transition: 'all 0.2s ease',
              }}
            >
              ✕ Reddet
            </Button>
          </Box>

          {/* Mesajlar sayfasına git linki */}
          <Button
            fullWidth
            onClick={() => {
              navigate(
                isDietitian ? '/dietitian/messages' : '/client/messages'
              );
            }}
            sx={{
              mt: 1,
              color: 'white',
              textDecoration: 'underline',
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Mesajlar sayfasına git
          </Button>
        </Paper>
      )}

      {/* Video Call Modal */}
      {isVideoCallOpen && incomingCall && (
        <VideoCall
          open={isVideoCallOpen}
          onClose={handleVideoCallClose}
          conversationId={incomingCall.conversationId}
          currentUserId={currentUserId}
          otherUserId={incomingCall.from}
          otherUserName={incomingCall.fromName}
          isInitiator={false}
        />
      )}
    </>
  );
};

export default IncomingCallNotification;
